import React, { useMemo } from 'react';

import { Descriptions, DescriptionsProps, Flex, Typography } from 'antd';

import dayjs from 'dayjs';
import { DATETIME_CLIENT_FORMAT, TIME_SERVER_FORMAT } from 'internal-constants';
import { UnexpectedTypeError } from 'lib/errors/UnexpectedTypeError';
import { LoadedInvite } from 'pages/invite/invite.type';

type Props = { invite: LoadedInvite };

/** @factory */
const makeInviteDateFriendlyString = (
    startDate: Invite.Model['start_date'],
    endDate: Invite.Model['end_date'],
    createdAt: Invite.Model['created_at'],
): string => {
    const parsedCreatedAt = dayjs(createdAt);
    const formattedParsedCreatedAt = parsedCreatedAt.format(TIME_SERVER_FORMAT);
    const [createdAtHours, createdAtMinutes] = formattedParsedCreatedAt.split(':').map(value => parseInt(value, 10));

    const parsedStartDate = dayjs(startDate);

    const isStartDateDayEqualsCreatedAtDay = parsedStartDate.isSame(parsedCreatedAt, 'day');

    const parsedStartDateWithTime = parsedStartDate
        .hour(isStartDateDayEqualsCreatedAtDay ? createdAtHours : 0)
        .minute(isStartDateDayEqualsCreatedAtDay ? createdAtMinutes : 59);

    const formattedParsedStartDate = parsedStartDateWithTime.format(DATETIME_CLIENT_FORMAT);

    const parsedEndDate = dayjs(endDate).hour(23).minute(59);

    const formattedParsedEndDate = parsedEndDate.format(DATETIME_CLIENT_FORMAT);

    const friendlyString = [formattedParsedStartDate, 'até', formattedParsedEndDate].join(' ');

    return friendlyString;
};

export function SignUpInviteDetails({ invite }: Props) {
    const items: DescriptionsProps['items'] = useMemo(() => {
        if (invite.apartment === null)
            throw new UnexpectedTypeError('invite.apartment', 'null', 'Apartment.Model');
    
        if (invite.author === null)
            throw new UnexpectedTypeError('invite.author', 'null', 'Resident.Model');

        return [
            {
                key: 'uuid',
                label: 'Identificador',
                children: invite.uuid,
            },
            {
                key: ['start_date', 'end_date', 'created_at'].join(),
                label: 'Período de acesso',
                children: makeInviteDateFriendlyString(invite.start_date, invite.end_date, invite.created_at),
            },
            {
                key: 'apartment',
                label: 'Unidade',
                children: [invite.apartment.tower.name, '-', invite.apartment.name].join(' '),
            },
            {
                key: 'author',
                label: 'Convidado por ',
                children: invite.author.name,
            },
        ];
    }, [invite]);

    return (
        <Flex vertical gap="middle">
            <Typography.Paragraph style={{ margin: 0, fontWeight: 600 }}>
                Detalhes do convite
            </Typography.Paragraph>

            <Descriptions
                items={items}
                column={2}
                layout="vertical"
                size="small"
                bordered
            />
        </Flex>
    );
}
